import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from '../../services/user.service';
import { ErrorService } from '../../services/error.service';
import { IUser } from '../../interfaces/user';
import { Permission } from '../../interfaces/permission.enum';
import { ISampleCorrectionView } from '../../interfaces/sample-correction-view';
import { NgForm } from '@angular/forms';
import { SampleCorrectionService } from '../../services/sample-correction.service';
import { ICreateSampleCorrectionCommand } from '../../interfaces/create-sample-correction-command';
import { ConfigurationService } from '../../services/configuration.service';
import { IExternalLinks } from '../../interfaces/external-links';

@Component({
    selector: 'app-sample-correction',
    templateUrl: './sample-correction.component.html',
    styleUrls: [],
    standalone: false
})
export class SampleCorrectionComponent implements OnInit {
  @ViewChild("createSampleCorrectionForm", { static: false }) private createSampleCorrectionForm: NgForm;
  @ViewChild("sidTextbox") sidTextbox: ElementRef = {} as ElementRef;

  public tempSampleId: string;
  private agencyId: number;

  public lastName: string;
  public firstName: string;
  public middleInitial: string;
  public suffix: string;
  public alias: string;
  public genderCode: string;
  public raceId: number;
  public dateOfBirth: NgbDateStruct;
  public ssn: string;
  public sid: string = "VA";
  public docNumber: string;
  public reason: string;

  public externalLinks: IExternalLinks;
  public data: ISampleCorrectionView;
  public errorMessage: string;
  public showVACORISNumber: boolean = false;
  public showValidation: boolean = false;
  public showThanks: boolean = false;
  public working: boolean = false;

  // Used for maxDate binding in date picker
  public get today(): NgbDateStruct {
    let today: Date = new Date();
    return { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() };
  }

  constructor(
    private readonly titleService: Title,
    private readonly route: ActivatedRoute,
    private readonly location: Location,
    private readonly userService: UserService,
    private readonly sampleCorrectionService: SampleCorrectionService,
    private readonly configService: ConfigurationService,
    private readonly errorService: ErrorService) { }

  ngOnInit() {
    this.titleService.setTitle("DNA Sample Correction");

    let currentUser: IUser = this.userService.getCurrentUser();
    this.agencyId = currentUser.agencyId;
    this.showVACORISNumber = currentUser.permissions.includes(Permission.ViewVACORISNumber);

    this.tempSampleId = this.route.snapshot.paramMap.get("id");
    this.sampleCorrectionService.getCreateView(this.tempSampleId)
      .subscribe(data => {
        this.data = data;
      }, error => {
        this.errorMessage = this.errorService.getErrorMessage(error);
      });

    this.configService.getExternalLinks()
      .subscribe(
        data => this.externalLinks = data,
        error => this.errorMessage = this.errorService.getErrorMessage(error)
      );
  }

  // Manage SID default value (place cursor after "VA" when tabbing in if only "VA" is there)
  public ssnKeyDown(e: KeyboardEvent): void {
    e.preventDefault();

    if (this.sid === "VA") {
      this.setInputSelection(this.sidTextbox.nativeElement, 2, 2);
    } else {
      this.setInputSelection(this.sidTextbox.nativeElement, 0, this.sid ? this.sid.length : 0);
    }
  }

  public goBack(e: MouseEvent): void {
    e.preventDefault();
    this.location.historyGo(-2);
  }

  public onSubmit(): void {
    // If the form is not valid, set a flag to show validation errors
    if (this.createSampleCorrectionForm.invalid) {
      this.showValidation = true;
      return;
    }

    this.working = true;

    const command: ICreateSampleCorrectionCommand = {
      agencyId: this.agencyId,
      sampleId: this.data.id,
      lastName: this.lastName ? this.lastName : null,
      firstName: this.firstName ? this.firstName : null,
      middleInitial: this.middleInitial ? this.middleInitial : null,
      suffix: this.suffix ? this.suffix : null,
      alias: this.alias ? this.alias : null,
      gender: this.genderCode,
      raceId: this.raceId,
      dateOfBirth: this.dateOfBirth ? new Date(Date.UTC(this.dateOfBirth.year, this.dateOfBirth.month - 1, this.dateOfBirth.day)) : null,
      ssn: this.ssn ? this.ssn : null,
      sid: this.sid && this.sid !== "VA" ? this.sid : null,
      docNumber: this.docNumber ? this.docNumber : null,
      reason: this.reason
    }

    this.sampleCorrectionService.create(command)
      .subscribe(() => {
        this.working = false;
        this.showThanks = true;
      }, error => {
        this.errorMessage = this.errorService.getErrorMessage(error);
        this.working = false;
      });
  }

  private setInputSelection(input: any, startPos: number, endPos: number): void {
    if (input.setSelectionRange) {
      input.focus();
      input.setSelectionRange(startPos, endPos);
    } else if (input.createTextRange) {
      var range = input.createTextRange();
      range.collapse(true);
      range.moveEnd('character', endPos);
      range.moveStart('character', startPos);
      range.select();
    }
  }
}
